import { useMemo } from 'react';
import { keepPreviousData, useMutation, useQuery } from '@tanstack/react-query';
import { queryClient } from '@/config/reactQuery';
import {
  AppSearch,
  AuditLogEvent,
  AuthorizationStatus,
  Damage,
  DamageCause,
  DamageCodesSelectData,
  Inspection,
  InspectionInsightScope,
  InternalGrading,
  VehicleInspectionInsights,
  RegularDamagePart,
  Select,
} from '@/types';
import { CarDamageAreaCode } from '@/utils/constants';

import { QUERY_KEYS } from '../../queryKeys';
import { inspections } from '../Inspections';

export const useInspectionsReportsInitial = (inspectionId: number) =>
  useQuery({
    queryKey: [QUERY_KEYS.inspectionsReportsInitial],
    queryFn: async () =>
      (await inspections.getInspectionsReportsInitial(inspectionId)).data,
    enabled: !!inspectionId,
  });

export const useInspectionsVehiclesFilters = () =>
  useQuery({
    queryKey: [QUERY_KEYS.inspectionsVehiclesFilters],
    queryFn: async () =>
      (await inspections.getInspectionsVehiclesFilters()).data,
  });

const serializeInspectionVehiclesQuery = (q: AppSearch<Inspection>) =>
  JSON.stringify({
    name: q.name,
    search: q.search,
    page: q.page,
    pageSize: q.pageSize,
    sortField: q.sortField,
    sortOrder: q.sortOrder,
    filters: q.filters,
  });

export const prepareInspectionVehiclesSearchBody = (
  queryData: AppSearch<Inspection>,
): AppSearch<Inspection> => {
  const body: AppSearch<Inspection> = JSON.parse(JSON.stringify(queryData));
  body.filters?.forEach((filter) => {
    filter.value = filter.value.map((v) =>
      v instanceof Date ? v.toISOString() : v?.toString(),
    );
  });
  return body;
};

export const useInspectionsVehiclesInsights = (
  query: AppSearch<Inspection> | undefined,
  options: {
    scope: InspectionInsightScope;
    chartPeriodDays: number;
  },
) => {
  const serialized = useMemo(
    () =>
      query
        ? JSON.stringify({
            scope: options.scope,
            chartPeriodDays: options.chartPeriodDays,
            search:
              options.scope === 'currentResults' ? query.search : undefined,
            filters: options.scope === 'currentResults' ? query.filters : [],
          })
        : '__idle__',
    [options.chartPeriodDays, options.scope, query],
  );

  return useQuery({
    queryKey: [QUERY_KEYS.inspectionsVehiclesInsights, serialized] as const,
    queryFn: async () => {
      const body = prepareInspectionVehiclesSearchBody(query!);
      const response = await inspections.postInspectionsVehiclesInsights({
        ...body,
        search: options.scope === 'currentResults' ? body.search : undefined,
        filters: options.scope === 'currentResults' ? body.filters : [],
        scope: options.scope,
        chartPeriodDays: options.chartPeriodDays,
      });
      return response?.data as VehicleInspectionInsights;
    },
    enabled: !!query,
    placeholderData: keepPreviousData,
  });
};

export const useInspectionsVehiclesList = (
  query: AppSearch<Inspection> | undefined,
  drill?: { insightCard?: string; insightAgeBand?: string },
) => {
  const serialized = useMemo(
    () =>
      query
        ? `${serializeInspectionVehiclesQuery(query)}|${drill?.insightCard || ''}|${drill?.insightAgeBand || ''}`
        : '__idle__',
    [drill?.insightAgeBand, drill?.insightCard, query],
  );

  return useQuery({
    queryKey: [QUERY_KEYS.inspectionsVehiclesList, serialized] as const,
    queryFn: async () => {
      const body = prepareInspectionVehiclesSearchBody(query!);
      const response = await inspections.postInspectionsVehicles({
        ...body,
        insightCard: drill?.insightCard,
        insightAgeBand: drill?.insightAgeBand,
      });
      return response?.data;
    },
    enabled: !!query,
    placeholderData: keepPreviousData,
  });
};

export const fetchInspectionsVehiclesForExport = async (
  query: AppSearch<Inspection>,
  includeDamages = false,
): Promise<{ entities: Inspection[]; damages: Damage[] }> => {
  const exportQuery = { ...query };
  exportQuery.page = undefined;
  exportQuery.pageSize = undefined;
  const body = prepareInspectionVehiclesSearchBody(
    exportQuery as AppSearch<Inspection>,
  );
  const response = await inspections.postInspectionsVehicles(
    includeDamages ? { ...body, includeDamages: true } : body,
  );
  return {
    entities: response?.data?.entities ?? [],
    damages: response?.data?.damages ?? [],
  };
};

export const invalidateInspectionsVehiclesList = () =>
  queryClient.invalidateQueries({
    queryKey: [QUERY_KEYS.inspectionsVehiclesList],
  });

export const refreshInspectionIfStatusChanged = (
  inspectionId: number,
  inspectionStatusChanged?: boolean | null,
) => {
  if (!inspectionStatusChanged) return;

  void queryClient.invalidateQueries({
    queryKey: [QUERY_KEYS.inspectionsVehicle, inspectionId],
  });
};

export const useUpdateInspectionsVehicle = (id: number) =>
  useMutation({
    mutationFn: inspections.updateInspectionsVehicle,
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.inspectionsVehicle, id],
      }),
  });

export const useInspectionsVehicle = (id: number) =>
  useQuery({
    queryKey: [QUERY_KEYS.inspectionsVehicle, id],
    queryFn: async () => {
      const res = await inspections.getInspectionsVehicle(id);
      if ((res as { errorResponse?: boolean }).errorResponse || !res.data) {
        throw new Error(res.message?.Message);
      }
      return res.data;
    },
    enabled: Number.isFinite(id) && id > 0,
  });

// GET Damages
export const usePreTimeSuggestions = (inspectionId: number, enabled = true) =>
  useQuery({
    queryKey: [QUERY_KEYS.preTimeSuggestions, inspectionId],
    queryFn: async () => {
      const res = await inspections.getPreTimeSuggestions(inspectionId);
      if (!res.success) {
        throw new Error(res.message?.Message || 'Unable to load suggestions');
      }
      return res.data ?? [];
    },
    enabled: enabled && Number.isFinite(inspectionId) && inspectionId > 0,
  });

export const useInspectionsVehiclesDamagesLines = (id: number) =>
  useQuery({
    queryKey: [QUERY_KEYS.damages, id],
    queryFn: async () => {
      const { data } = await inspections.getInspectionsVehiclesDamagesLines(id);
      if (data)
        data.forEach((item) => {
          const typedColorString =
            item.authorizationStatus as keyof typeof AuthorizationStatus;
          const status = AuthorizationStatus[typedColorString];
          item.authorizationStatusNum = status;
        });
      return data;
    },
  });

export const useDamageLineById = (
  inspectionId: number,
  damageLineId?: string,
) =>
  useQuery({
    queryKey: [QUERY_KEYS.damageItem, inspectionId, damageLineId],
    queryFn: async () => {
      const { data } =
        await inspections.getInspectionsVehiclesDamagesLines(inspectionId);
      if (!data || !damageLineId) return null;

      const damageLine = data.find(
        (item) => item.id.toString() === damageLineId,
      );
      return damageLine || null;
    },
    enabled: !!inspectionId && !!damageLineId,
  });

export const useUpdateInspectionsVehicleDamageAuthorization = () =>
  useMutation({
    mutationFn: inspections.updateDamageAuthorization,
  });

export const useResolveVehicleDamagePreTimingReview = () =>
  useMutation({
    mutationFn: inspections.resolvePreTimingReview,
  });

export const useVehicleDamageClassificationChangeSummary = (
  inspectionId: number,
  enabled: boolean,
) =>
  useQuery({
    queryKey: [QUERY_KEYS.damageClassificationChangeSummary, inspectionId],
    queryFn: async () => {
      const res =
        await inspections.getVehicleDamageClassificationChangeSummary(
          inspectionId,
        );
      return res.data ?? null;
    },
    enabled: !!inspectionId && enabled,
  });

export const useBatchUpdateInspectionsVehicleDamagesStatus = () =>
  useMutation({
    mutationFn: inspections.batchUpdateInspectionsVehicleDamagesStatus,
  });

export const useUpdateInspectionVehicleDamageDefaultStatus = () =>
  useMutation({
    mutationFn: inspections.updateInspectionVehicleDamageDefaultStatus,
  });

export const useAddInspectionsVehicleDamageActions = () =>
  useMutation({
    mutationFn: inspections.addInspectionsVehicleDamageActions,
  });

export const useAddInspectionsVehicleDamageParts = () =>
  useMutation({ mutationFn: inspections.addInspectionsVehicleDamageParts });

export const useInspectionsVehicleWorkCentres = (inspectionId: number) =>
  useQuery({
    queryKey: [QUERY_KEYS.workCenters, inspectionId],
    queryFn: async () => {
      const { data } =
        await inspections.getInspectionsVehicleWorkCentres(inspectionId);
      return (
        data?.map((workCenter) => ({
          label: `${workCenter.description} - ${workCenter.code}`,
          value: workCenter.code,
        })) || []
      );
    },
    enabled: !!inspectionId,
  });

export const useInspectionsSearchParts = () =>
  useMutation({ mutationFn: inspections.getInspectionsVehiclesParts });

export const useInspectionSearchFlatRateCode = () =>
  useMutation({ mutationFn: inspections.getInspectionsVehiclesFlatRateCode });

export const useInspectionSearchLabour = (inspectionId: number) =>
  useQuery({
    queryKey: [QUERY_KEYS.supplier, inspectionId],
    queryFn: async () =>
      (await inspections.getInspectionsVehiclesLabour(inspectionId)).data,
  });

export const useInspectionSearchSupplier = () =>
  useQuery({
    queryKey: [QUERY_KEYS.supplier],
    queryFn: async () =>
      (await inspections.getInspectionsVehiclesSupplier()).data,
  });

export const useInspectionSearchSupplierParts = () =>
  useMutation({ mutationFn: inspections.getInspectionsVehiclesSupplierPart });

export const useInspectionsVehicleDamageParts = (
  inspectionId: number,
  options?: {
    enabled?: boolean;
  },
) =>
  useQuery({
    queryKey: [QUERY_KEYS.damageParts, inspectionId],
    queryFn: async () => {
      const response =
        await inspections.getInspectionsVehicleDamageParts(inspectionId);

      const damageParts =
        response?.data
          ?.filter(({ areaCode, partCode }) => areaCode && partCode)
          .sort((a, b) => {
            if (
              a.partDescription?.toLowerCase() <
              b.partDescription?.toLowerCase()
            ) {
              return -1;
            }
            if (
              a.partDescription?.toLowerCase() >
              b.partDescription?.toLowerCase()
            ) {
              return 1;
            }
            return 0;
          }) || [];

      const mapToSelectItemsGroupedByCarArea = (areas: RegularDamagePart[]) =>
        areas.reduce(
          (
            acc: Record<CarDamageAreaCode, Select[]>,
            { partCode, partDescription, areaCode }: RegularDamagePart,
          ) => ({
            ...acc,
            [areaCode]: [
              ...(acc[areaCode] || []),
              {
                value: partCode,
                label: partDescription,
              },
            ],
          }),
          {} as Record<CarDamageAreaCode, Select[]>,
        );

      return {
        selectItems: mapToSelectItemsGroupedByCarArea(damageParts),
        damageParts,
        getPartByCode: (partCode?: string) =>
          damageParts.find((part) => partCode === part.partCode),
      };
    },
    enabled: options?.enabled,
  });

export const useDeleteInspectionsVehicleDamageAction = () =>
  useMutation({
    mutationFn: inspections.deleteInspectionsVehiclesDamageAction,
  });

export const useDeleteInspectionsVehicleDamagePart = () =>
  useMutation({ mutationFn: inspections.deleteInspectionsVehiclesDamagePart });

export const useCreateInspectionsVehiclesFlatRateCode = () =>
  useMutation({
    mutationFn: inspections.createInspectionsVehiclesFlatRateCode,
  });

export const useInspectionsVehiclesNotes = (id: number) =>
  useQuery({
    queryKey: [QUERY_KEYS.inspectionsVehiclesNotes, id],
    queryFn: async () =>
      (await inspections.getInspectionsVehiclesNotes(id)).data,
  });

export const useCreateInspectionsVehiclesNotes = (id: number) =>
  useMutation({
    mutationFn: (comment: string) =>
      inspections.createInspectionsVehiclesNotes(id, { comment }),
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.inspectionsVehiclesNotes, id],
      }),
  });

export const useInspectionsLogs = (id: number) =>
  useQuery({
    queryKey: [QUERY_KEYS.logs],
    queryFn: async () => {
      const { data } = await inspections.getInspectionsLogs(id);
      if (data)
        data.entities.forEach((item) => {
          item.originalEventType = item.eventType;
          item.eventType =
            AuditLogEvent[
              item.eventType as unknown as keyof typeof AuditLogEvent
            ];
        });
      return data;
    },
  });

export const useInspectionsChecklistDynamicOperations = (id: number) =>
  useQuery({
    queryKey: [QUERY_KEYS.inspectionsChecklistDynamicOperations, id],
    queryFn: async () =>
      (await inspections.getInspectionsChecklistDynamicOperations(id)).data,
  });

export const useInspectionsChecklistElectricHybrid = (id: number) =>
  useQuery({
    queryKey: [QUERY_KEYS.inspectionsChecklistElectricHybrid, id],
    queryFn: async () =>
      (await inspections.getInspectionsChecklistElectricHybrid(id)).data,
  });

export const useInspectionsChecklistEngineRunning = (id: number) =>
  useQuery({
    queryKey: [QUERY_KEYS.inspectionsChecklistEngineRunning, id],
    queryFn: async () =>
      (await inspections.getInspectionsChecklistEngineRunning(id)).data,
  });

export const useInspectionsChecklistInterior = (id: number) =>
  useQuery({
    queryKey: [QUERY_KEYS.inspectionsChecklistInterior, id],
    queryFn: async () =>
      (await inspections.getInspectionsChecklistInterior(id)).data,
  });

export const useInspectionDamagesImages = (id: number) =>
  useQuery({
    queryKey: [QUERY_KEYS.inspectionDamageImage, id],
    queryFn: async () =>
      (await inspections.getInspectionsDamagesImages(id)).data,
    staleTime: Infinity,
  });

export const useUpdateInspectionDamagesImages = (id: number) =>
  useMutation({
    mutationFn: inspections.uploadInspectionsDamagesImages,
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.inspectionDamageImage, id],
      }),
  });

export const useDeleteInspectionDamagesImages = () =>
  useMutation({
    mutationFn: inspections.deleteInspectionsDamagesImage,
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.inspectionDamageImage],
      }),
  });

export const useInspectionsVehiclesSummaries = (id: number) =>
  useQuery({
    queryKey: [QUERY_KEYS.inspectionsVehiclesSummaries, id],
    queryFn: async () =>
      (await inspections.getInspectionsVehiclesSummaries(id)).data,
  });

export const useInspectionsVehiclesMechanicalsNotes = (id: number) =>
  useQuery({
    queryKey: [QUERY_KEYS.inspectionsMechanicalsNotes, id],
    queryFn: async () =>
      (await inspections.getInspectionsVehiclesMechanicalsNotes(id)).data,
  });

export const useInspectionsMechanicalsWheels = (id: number) =>
  useQuery({
    queryKey: [QUERY_KEYS.inspectionsMechanicalsWheels, id],
    queryFn: async () =>
      (await inspections.getInspectionsMechanicalsWheels(id)).data,
  });

export const useInspectionsVehicleDamageCodes = (
  id: number,
  siteId?: number | string,
) =>
  useQuery({
    queryKey: [QUERY_KEYS.damageCodes, id, siteId],
    queryFn: async () => {
      const { data } = await inspections.getInspectionsVehicleDamageCodes(
        id,
        siteId!,
      );

      const mapToSelectItem = (damage: DamageCause): Select => ({
        label: damage?.description,
        value: damage?.code,
      });

      const mapInternalGradingToSelectItem = (
        internalGrading: InternalGrading,
      ): Select => ({
        label: internalGrading?.description,
        value: internalGrading?.internalGradingId,
      });

      return {
        damageCauses: data?.damageCauses?.map(mapToSelectItem),
        internalGradings: data?.internalGradings?.map(
          mapInternalGradingToSelectItem,
        ),
        responsibilities: {
          options: data?.responsibilities?.map(mapToSelectItem),
          getItem: (value: string) =>
            data?.responsibilities?.find(
              (responsibility) => responsibility?.code === value,
            ),
          getResponsibilityShortcutsOptions: (responsibilityCode?: string) =>
            data?.responsibilities
              ?.find(
                (responsibility) => responsibility?.code === responsibilityCode,
              )
              ?.availableResponsibilityShortcuts?.map(mapToSelectItem),
          getDealersOptions: (responsibilityCode?: string) =>
            data?.responsibilities
              ?.find(
                (responsibility) => responsibility?.code === responsibilityCode,
              )
              ?.availableDealers?.map(mapToSelectItem),
        },
        workCenters: data?.workCenters?.map(
          (workCenter: DamageCause): Select => ({
            label: `${workCenter?.description} - ${workCenter?.code}`,
            value: workCenter?.code,
          }),
        ),
      } as DamageCodesSelectData;
    },
    enabled: !!siteId,
  });

export const useInspectionsVehicleDamageConditionCodes = (
  id: number,
  partCode: string | number,
) =>
  useQuery({
    queryKey: [QUERY_KEYS.damageConditionCodes, id, partCode],
    queryFn: async () =>
      (
        await inspections.getInspectionsVehicleDamageConditionCodes(
          id,
          partCode,
        )
      )?.data || [],
    enabled: !!partCode,
  });

export const useInspectionsVehicleSeverityCodes = (
  id: number,
  partCode: string | number,
  typeCode: string | number,
) =>
  useQuery({
    queryKey: [QUERY_KEYS.damageSeverityCodes, id, partCode, typeCode],
    queryFn: async () =>
      (
        await inspections.getInspectionsVehicleSeverityCodes(
          id,
          partCode,
          typeCode,
        )
      )?.data || [],
    enabled: !!partCode && !!typeCode,
  });

export const useInspectionsVehicleRepairActionCodes = (
  id: number,
  partCode: string | number,
  typeCode: string | number,
  severityId?: number,
) =>
  useQuery({
    queryKey: [
      QUERY_KEYS.damageRepairActionCodes,
      id,
      partCode,
      typeCode,
      severityId,
    ],
    queryFn: async () =>
      (
        await inspections.getInspectionsVehicleRepairActionCodes(
          id,
          partCode,
          typeCode,
          severityId || 0,
        )
      )?.data || [],
    enabled: !!partCode && !!typeCode && (!!severityId || severityId === 0),
  });

export const useCreateInspectionsVehiclesDamagesLines = () =>
  useMutation({
    mutationFn: inspections.createInspectionsVehiclesDamagesLines,
  });

export const useBatchUpdateInspectionsVehiclesDamagesLines = () =>
  useMutation({
    mutationFn: inspections.batchUpdateInspectionsVehiclesDamagesLines,
  });

export const useDeleteInspectionsVehiclesDamagesLines = () =>
  useMutation({
    mutationFn: inspections.deleteInspectionsVehiclesDamagesLines,
  });

export const useUnDeleteInspectionsVehiclesDamagesLines = () =>
  useMutation({
    mutationFn: inspections.unDeleteInspectionsVehiclesDamagesLines,
  });

export const useCreateTNROverride = () =>
  useMutation({ mutationFn: inspections.postTNROverride });

export const useUpdateTNRPreAppraisal = () =>
  useMutation({ mutationFn: inspections.updateTNRPreAppraisal });

export const useUpdateTNRPostAppraisal = () =>
  useMutation({ mutationFn: inspections.updateTNRPostAppraisal });

export const useTNRDetails = (id: number) =>
  useQuery({
    queryKey: [QUERY_KEYS.tnrdetails, id],
    queryFn: async () => (await inspections.getTNRDetails(id)).data,
  });

export const useInspectionMOT = (id: number) =>
  useMutation({
    mutationFn: async () => (await inspections.getInspectionMOT(id)).data,
  });

export const useReplaceImage = () =>
  useMutation({ mutationFn: inspections.postReplaceImage });

export const useUpdateInspectionMileage = () =>
  useMutation({ mutationFn: inspections.updateInspectionMileage });

export const useUpdateInspectionReg = () =>
  useMutation({ mutationFn: inspections.updateInspectionReg });

export const useUpdateInspectionMechanicalInspection = () =>
  useMutation({
    mutationFn: inspections.updateInspectionMechanicalInspection,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [
          QUERY_KEYS.inspectionsChecklistDynamicOperations,
          variables.id,
        ],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.inspectionsChecklistElectricHybrid, variables.id],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.inspectionsChecklistEngineRunning, variables.id],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.inspectionsChecklistInterior, variables.id],
      });
    },
  });

export const useGetFprInspectionDetail = (vin: string) =>
  useQuery({
    queryKey: [QUERY_KEYS.fprInspection, vin],
    queryFn: async () => (await inspections.getFprInspectionDetail(vin)).data,
    enabled: !!vin.length,
  });
